import * as React from 'react';

import {
  ActivityStage,
  ActivityType,
  QuestionTypes,
} from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';

import { ToggleButton, UIButton } from '~shared/components/buttons';
import { Inline } from '~shared/components/Inline';
import { Student, StudentActivityResponse } from '~teacher/models/activities';
import { QuizQuestion } from '~teacher/models/quizzes';

import { useResultQuestionState } from '../ActivityResult/ActivityResultQuestionProvider';

interface Props {
  showResults?: boolean;
  setShowResults?: React.Dispatch<React.SetStateAction<boolean>>;
  showAnswers?: boolean;
  setShowAnswers?: React.Dispatch<React.SetStateAction<boolean>>;
  showNames?: boolean;
  setShowNames?: React.Dispatch<React.SetStateAction<boolean>>;
  onStartVoteClicked: React.Dispatch<React.SetStateAction<boolean>>;
}

export function ResultsQuestionOptions(props: Props): React.ReactElement {
  const questionContext = useResultQuestionState();
  const {
    question = {} as QuizQuestion,
    responses,
    students,
    numParticipantsResponded,
    activityType,
    activityStage,
    roomHasRoster,
  } = questionContext;

  return (
    <ResultsQuestionOptionsPure
      {...props}
      question={question}
      responses={responses}
      students={students}
      numParticipantsResponded={numParticipantsResponded}
      activityType={activityType}
      activityStage={activityStage}
      roomHasRoster={roomHasRoster}
    />
  );
}

export function ResultsQuestionOptionsPure({
  question,
  responses,
  showResults,
  setShowResults,
  showAnswers,
  setShowAnswers,
  showNames,
  setShowNames,
  onStartVoteClicked,
  students,
  numParticipantsResponded,
  activityType,
  activityStage,
  roomHasRoster,
}: Props & {
  question: QuizQuestion;
  responses: StudentActivityResponse[];
  students: Record<string, Student>;
  numParticipantsResponded: number;
  activityType: ActivityType;
  activityStage: ActivityStage;
  roomHasRoster: boolean;
}): React.ReactElement {
  if (
    (activityType === ActivityType.Quiz ||
      activityType === ActivityType.SpaceRace) &&
    (question.type === QuestionTypes.MultipleChoice ||
      question.type === QuestionTypes.TrueFalse)
  ) {
    return (
      <div className="flex items-center pt-8">
        <Inline space="4" alignY="center">
          <ToggleButton
            id="results-toggle"
            pressed={showResults}
            pressedLabel={translate('Hide Results')}
            onToggle={setShowResults}
          >
            {translate('Show Results')}
          </ToggleButton>
          <span className="font-sans text-sm text-blue-900 capitalize">
            {`{0}/{1} ${translate('students answered')}`.format(
              numParticipantsResponded,
              Object.values(students ?? {}).length
            )}
          </span>
        </Inline>
      </div>
    );
  }

  if (
    (activityType === ActivityType.Quiz ||
      activityType === ActivityType.SpaceRace) &&
    question.type === QuestionTypes.FreeResponse
  ) {
    return (
      <div className="flex items-center pt-8">
        <Inline space="4" alignY="center">
          <ToggleButton
            id="answers-toggle"
            pressed={showAnswers}
            pressedLabel={translate('Hide Answers')}
            onToggle={setShowAnswers}
          >
            {translate('Show Answers')}
          </ToggleButton>
          <ToggleButton
            id="answers-toggle"
            pressed={showNames}
            pressedLabel={translate('Hide Names')}
            onToggle={setShowNames}
          >
            {translate('Show Names')}
          </ToggleButton>
          <span className="font-sans text-sm text-blue-900 capitalize">
            {`{0}/{1} ${translate('students answered')}`.format(
              numParticipantsResponded,
              Object.values(students ?? {}).length
            )}
          </span>
        </Inline>
      </div>
    );
  }

  if (activityType === ActivityType.QuickQuestionShortAnswer) {
    return (
      <div className="flex items-center pt-8">
        <Inline space="4" alignY="center">
          {ActivityStage.InProgress === activityStage && (
            <UIButton onClick={onStartVoteClicked}>
              {translate('Start Vote')}
            </UIButton>
          )}
          <ToggleButton
            id="answers-toggle"
            pressed={showAnswers}
            pressedLabel={translate('Hide Answers')}
            onToggle={setShowAnswers}
          >
            {translate('Show Answers')}
          </ToggleButton>
          <ToggleButton
            id="answers-toggle"
            pressed={showNames}
            pressedLabel={translate('Hide Names')}
            onToggle={setShowNames}
          >
            {translate('Show Names')}
          </ToggleButton>
          <span className="font-sans text-sm text-blue-900 capitalize">
            {roomHasRoster
              ? `{0}/{1} ${translate('students answered')}`.format(
                  numParticipantsResponded,
                  Object.values(students ?? {}).length
                )
              : `{0} ${translate('answers')}`.format(responses.length)}
          </span>
        </Inline>
      </div>
    );
  }

  if (activityType === ActivityType.Vote && !roomHasRoster) {
    return (
      <div className="flex items-center pt-8">
        <Inline space="4" alignY="center">
          <span className="font-sans text-sm text-blue-900 capitalize">
            {`{0} ${translate('answers')}`.format(responses.length)}
          </span>
        </Inline>
      </div>
    );
  }

  return (
    <div className="flex items-center pt-8">
      <Inline space="4" alignY="center">
        <span className="font-sans text-sm text-blue-900 capitalize">
          {`{0}/{1} ${translate('students answered')}`.format(
            numParticipantsResponded,
            Object.values(students ?? {}).length
          )}
        </span>
      </Inline>
    </div>
  );
}
